import assert from 'assert';
import { Str } from '@livecontrol/core-utils';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useRouteHistory } from '../../store';
import { EventLayout } from './layout';

const EVENTS_ROUTE = '/events';
const LIVE_EVENTS_ROUTE = '/events/live';

/*
 * Where "Back to Dashboard" lands. It has to be an explicit destination rather
 * than history.goBack(): the previous page can be another event, an asset, or a
 * deep link with nothing behind it, and the button promises the dashboard.
 *
 * Coming from any dashboard list returns you to that same list; everything else
 * lands on Live. Matching the /events prefix instead of listing the tabs keeps
 * this working when a tab is added or renamed — the event page itself is
 * /event/:eid, singular, so there is no overlap.
 */
const dashboardRoute = (pathname?: string): string =>
  pathname === EVENTS_ROUTE || pathname?.startsWith(`${EVENTS_ROUTE}/`) === true
    ? pathname
    : LIVE_EVENTS_ROUTE;

const Page = styled.div`
  padding: 4px 30px 32px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 2px;

  .back {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: var(--white40);
    font-size: 14px;
    font-weight: 500;
    transition: color var(--transition);

    &:hover,
    &:focus {
      outline: none;
      color: var(--white);
    }
  }

  .tz {
    color: var(--white40);
    font-size: 13px;
    white-space: nowrap;

    b {
      font-weight: 700;
    }
  }
`;

export const EventDetailsV2 = (): ReactElement => {
  const history = useHistory();
  const { prevPathname } = useRouteHistory();
  const eid = Str.normalize(useParams<{ eid: string }>().eid);

  assert(typeof eid === 'string');

  return (
    <Page>
      <TopBar>
        <button
          type='button'
          className='x-pseudo back'
          onClick={(): void => {
            history.push(dashboardRoute(prevPathname));
          }}
        >
          <i className='fas fa-long-arrow-left' />
          Back to Dashboard
        </button>

        <div className='tz'>
          <b>Time Zone:</b> {DateTime.local().toFormat('z')}
        </div>
      </TopBar>

      <EventLayout eid={eid} />
    </Page>
  );
};
